import {useEffect, useState} from 'react';

import {Grid, Typography} from '@mui/material';

import {IIPENSessionData} from '../../../types/IPENSessionData';
import {IOrderType} from '../../../types/OrderOverview';
import {OrderStatus} from '../../constants/OrderStatus';
import {gray, primaryBlue, secondaryBlue} from '../../constants/colors';
import {IItemStatus} from '../../type';
import Legend from '../Legend';
import NotaryPreClosing from '../NotaryPreClosing';
import OnClosing from '../OnClosing';
import OrdersActionMessageAlarm from '../OrdersActionMessageAlarm';
import SAPreClosing from '../SAPreClosing';

interface IProps {
  orderStatus: string;
  orderType: IOrderType;
  notaryAssigned: boolean;
  signerEvents: boolean;
  isOrderReviewedBySA: boolean;
  isDocumentReviewedBySA: boolean;
  isOrderReviewedByNotary: boolean;
  isIdentifyWitnessPartyByNotary: boolean;
  isDocumentReviewedByNotary: boolean;
  notarySigned: boolean;
  docsTagged: boolean;
  docsEdited: boolean;
  isCurrentUserEscrowOnOrder: boolean;
  isCurrentUserNotaryOnOrder: boolean;
  orderContainsWitnessSigners: boolean;
  orderContainsNPE?: boolean;
  preClosingActive: boolean;
  onClosingActive: boolean;
  postClosingActive: boolean;
  closingDate: string;
  ipenSession: any;
  ipenSessionData: IIPENSessionData | null;
  setOpenWitnessInfoModal: (isOpen: boolean) => void;
  onSaActionsClick: (i: any) => void;
  onNotaryActionsClick: (i: any) => void;
  setOpenIPENSessionModal: (isOpen: boolean) => void;
}

function SolexIPENActions(props: IProps) {
  const {
    orderStatus,
    orderType,
    signerEvents,
    isOrderReviewedBySA,
    isDocumentReviewedBySA,
    isOrderReviewedByNotary,
    isDocumentReviewedByNotary,
    isIdentifyWitnessPartyByNotary,
    notarySigned,
    notaryAssigned,
    docsEdited,
    docsTagged,
    isCurrentUserEscrowOnOrder,
    isCurrentUserNotaryOnOrder,
    orderContainsWitnessSigners,
    orderContainsNPE,
    preClosingActive,
    onClosingActive,
    postClosingActive,
    closingDate,
    ipenSession,
    ipenSessionData,
    setOpenWitnessInfoModal,
    onSaActionsClick,
    onNotaryActionsClick,
    setOpenIPENSessionModal,
  } = props;

  const initialPreClosingSettlementItems: IItemStatus[] = [
    {
      name: 'Review Order Details and Party Information',
      disabled: true,
      completed: false,
    },
    { name: 'Review Document Package', disabled: true, completed: false },
    {
      name: 'Upload and Tag Title Documents',
      disabled: true,
      completed: false,
    },
    { name: 'Assign Notary *', disabled: true, completed: false },
  ];

  const initialPreClosingNotaryItems: IItemStatus[] = [
    {
      name: 'Review Order Details and Party Information',
      disabled: true,
      completed: false,
    },
    { name: 'Review Document Package', disabled: true, completed: false },
  ];

  const initialOnClosingNotaryItems: IItemStatus[] = [
    ...(orderContainsWitnessSigners ? [
      {
        name: 'Identify Witness Parties *',
        disabled: true,
        completed: false,
      }
    ] : []),
    {
      name: 'Launch IPEN Session *',
      disabled: true,
      completed: orderStatus === OrderStatus.OpenAllSigned,
    },
  ];

  const [preClosingSettlementItems, setPreClosingSettlementItems] = useState<
    IItemStatus[]
  >(initialPreClosingSettlementItems);
  const [preClosingNotaryItems, setPreClosingNotaryItems] = useState<
    IItemStatus[]
  >(initialPreClosingNotaryItems);
  const [onClosingNotaryItems, setOnClosingNotaryItems] = useState<
    IItemStatus[]
  >(initialOnClosingNotaryItems);
  const [onClosingNotarySubItems, setOnClosingNotarySubItems] = useState<
    IItemStatus[]
  >([]);
  const partyLink = ipenSessionData?.tagDocsLink;
  const orderStatusIsOpenLinkSent: boolean =
    orderStatus === OrderStatus.OpenLinksSent;

  // set up pre closing SA items
  useEffect(() => {
    const updatedPreClosingSAItems: IItemStatus[] = [
      ...preClosingSettlementItems,
    ];
    const reviewOrder = updatedPreClosingSAItems.find(
      (item) => item.name === 'Review Order Details and Party Information'
    );
    const reviewDocs = updatedPreClosingSAItems.find(
      (item) => item.name === 'Review Document Package'
    );
    const uploadTitleDocs = updatedPreClosingSAItems.find(
      (item) => item.name === 'Upload and Tag Title Documents'
    );
    const assignNotary = updatedPreClosingSAItems.find(
      (item) => item.name === 'Assign Notary *'
    );

    if (!reviewOrder) return;
    reviewOrder.completed = isOrderReviewedBySA;
    reviewOrder.disabled = !orderStatusIsOpenLinkSent;

    if (!reviewDocs) return;
    reviewDocs.completed = isDocumentReviewedBySA;
    reviewDocs.disabled = !orderStatusIsOpenLinkSent;

    if (!uploadTitleDocs) return;
    uploadTitleDocs.completed = docsTagged && docsEdited;
    uploadTitleDocs.disabled =
      (docsTagged && docsEdited) || !partyLink || !orderStatusIsOpenLinkSent;

    if (!assignNotary) return;
    assignNotary.completed = notaryAssigned;
    assignNotary.disabled = notaryAssigned || !orderStatusIsOpenLinkSent;

    setPreClosingSettlementItems(updatedPreClosingSAItems);
  }, [
    isOrderReviewedBySA,
    isDocumentReviewedBySA,
    notaryAssigned,
    docsTagged,
    docsEdited,
    orderStatus,
    partyLink,
  ]);

  // Set up pre closing Notary items
  useEffect(() => {
    const updatedPreClosingNotaryItems: IItemStatus[] = [
      ...preClosingNotaryItems,
    ];
    const reviewOrder = updatedPreClosingNotaryItems.find(
      (item) => item.name === 'Review Order Details and Party Information'
    );
    const reviewDocs = updatedPreClosingNotaryItems.find(
      (item) => item.name === 'Review Document Package'
    );

    if (!reviewOrder) return;
    reviewOrder.completed = isOrderReviewedByNotary;
    reviewOrder.disabled = !orderStatusIsOpenLinkSent;

    if (!reviewDocs) return;
    reviewDocs.completed = isDocumentReviewedByNotary;
    reviewDocs.disabled = !orderStatusIsOpenLinkSent;

    setPreClosingNotaryItems(updatedPreClosingNotaryItems);
  }, [orderStatus, isOrderReviewedByNotary, isDocumentReviewedByNotary]);

  //Set on closing Notary items
  useEffect(() => {
    const updatedOnClosingNotaryItems: IItemStatus[] = [
      ...onClosingNotaryItems,
    ];
    const launchSessionItem = updatedOnClosingNotaryItems.find(
      (item) =>
        item.name === 'Launch IPEN Session *' ||
        item.name === 'View IPEN Session *'
    );
    const identifyWitnessItem = updatedOnClosingNotaryItems.find(
      (item) => item.name === 'Identify Witness Parties *'
    );

    if (!launchSessionItem && !identifyWitnessItem) return;

    if (launchSessionItem) {
      launchSessionItem.disabled =
        !orderStatusIsOpenLinkSent ||
        !isCurrentUserNotaryOnOrder ||
        !onClosingActive ||
        postClosingActive ||
        preClosingActive ||
        (!isIdentifyWitnessPartyByNotary && orderContainsWitnessSigners) ||
        (ipenSession.isSessionComplete && notarySigned);

      launchSessionItem.completed = ipenSession.isSessionComplete;
      launchSessionItem.name = ipenSession.isActiveSession
        ? 'View IPEN Session *'
        : 'Launch IPEN Session *';
    }

    if (identifyWitnessItem) {
      identifyWitnessItem.completed = isIdentifyWitnessPartyByNotary;
      identifyWitnessItem.disabled =
        !onClosingActive ||
        isIdentifyWitnessPartyByNotary ||
        !orderStatusIsOpenLinkSent ||
        !isCurrentUserNotaryOnOrder;
    }

    setOnClosingNotaryItems(updatedOnClosingNotaryItems);
  }, [
    onClosingActive,
    postClosingActive,
    ipenSession.isSessionComplete,
    ipenSession.isActiveSession,
    orderStatus,
    notarySigned,
    isCurrentUserNotaryOnOrder,
    isIdentifyWitnessPartyByNotary,
    preClosingActive,
  ]);

  useEffect(function populateOnClosingSubItems() {
    const isOnClosingEnabled = isCurrentUserNotaryOnOrder && orderStatusIsOpenLinkSent && onClosingActive;
    const subItems: IItemStatus[] = [];

    subItems.push({ name: 'Facilitate eSign Sessions', disabled: !isOnClosingEnabled, completed: ipenSessionData?.allSignersSigned ?? false });
    subItems.push(signerEvents && notarySigned
        ? { name: 'Documents eNotarized', completed: true, disabled: !isOnClosingEnabled }
        : { name: 'eNotarize Documents', completed: false, disabled: !isOnClosingEnabled }
    );

    setOnClosingNotarySubItems(subItems);
  }, [
    ipenSessionData,
    ipenSession,
    notarySigned,
    isCurrentUserNotaryOnOrder,
    orderStatus,
    signerEvents,
    orderContainsWitnessSigners,
    orderContainsNPE,
  ]);

  return (
    <>
      <Grid
        container
        sx={{ backgroundColor: secondaryBlue, justifyContent: 'center' }}>
        <Grid
          item
          xs={1.5}
          sm={1.5}
          md={1.5}
          lg={1}
          xl={1}
          sx={{
            display: 'inline-block',
            alignSelf: 'center',
            borderRight: 1,
            borderColor: gray,
          }}>
          <Typography color={gray}>
            <strong>Order Type:</strong>
          </Typography>
          <Typography color={primaryBlue}>
            <strong>{orderType.Name}</strong>
          </Typography>
        </Grid>
        <Grid
          item
          xs={9.5}
          sm={9.5}
          md={9.5}
          lg={8}
          xl={8}
          sx={{ paddingLeft: 1 }}>
          <OrdersActionMessageAlarm
            preClosingActive={preClosingActive}
            onClosingActive={onClosingActive}
            postClosingActive={postClosingActive}
            isCurrentUserEscrowOnOrder={isCurrentUserEscrowOnOrder}
            isCurrentUserNotaryOnOrder={isCurrentUserNotaryOnOrder}
            preClosingSettlementItems={preClosingSettlementItems}
            preClosingNotaryItems={preClosingNotaryItems}
            onClosingNotaryItems={onClosingNotaryItems}
            onClosingNotarySubItems={onClosingNotarySubItems}
            ipenSession={ipenSession}
            orderStatus={orderStatus}
            orderType={orderType}
          />
        </Grid>
      </Grid>
      <Grid
        container
        sx={{ paddingTop: 3, paddingBottom: 1, justifyContent: 'center' }}>
        {isCurrentUserEscrowOnOrder && (
          <SAPreClosing
            isCurrentUserEscrowOnOrder={isCurrentUserEscrowOnOrder}
            preClosingActive={preClosingActive}
            preClosingSettlementItems={preClosingSettlementItems}
            onSaActionsClick={(i) => onSaActionsClick(i)}
          />
        )}
        {isCurrentUserNotaryOnOrder && !isCurrentUserEscrowOnOrder && (
          <NotaryPreClosing
            isCurrentUserNotaryOnOrder={isCurrentUserNotaryOnOrder}
            onClosingActive={onClosingActive}
            preClosingNotaryItems={preClosingNotaryItems}
            onNotaryActionsClick={(i) => onNotaryActionsClick(i)}
          />
        )}
        <OnClosing
          isCurrentUserNotaryOnOrder={isCurrentUserNotaryOnOrder}
          orderContainsWitnessSigners={orderContainsWitnessSigners}
          closingDate={closingDate}
          preClosingActive={preClosingActive}
          onClosingActive={onClosingActive}
          onClosingNotaryItems={onClosingNotaryItems}
          onClosingNotarySubItems={onClosingNotarySubItems}
          setOpenIPENSessionModal={(v) => setOpenIPENSessionModal(v)}
          setOpenWitnessInfoModal={setOpenWitnessInfoModal}
        />
        <Grid item xs={9.1} sx={{ paddingBottom: 2 }}>
          <Legend />
        </Grid>
      </Grid>
    </>
  );
}

export default SolexIPENActions;
